// sku-segment-helpers.js — pure helpers + constants shared by the SKU rule
// wizard (SkuFamilyBuilderDialog) and its sub-components (SegmentPicker).
// Extracted from SkuFamilyBuilderDialog.js in iter_145 to keep the main
// dialog file below the 700-line guideline.
//
// Nothing here touches React state or network calls — everything is a pure
// data transformation, making these helpers safe to unit-test in isolation.

import {
  TextAa, Hash, CalendarBlank, CalendarCheck, CalendarStar, ListNumbers, Divide,
} from '@phosphor-icons/react';

// ── Segment "types" the user sees in the picker ─────────────────────────
export const SEG_TYPES = [
  {
    key: 'letters',
    label: '固定文字',
    hint: '入力した文字がそのまま出ます  例: SMH → 「SMH」',
    icon: TextAa,
    tone: 'from-blue-50 to-indigo-50 border-blue-200 text-blue-800',
    dot: 'bg-blue-500',
  },
  {
    key: 'digits',
    label: '固定数字',
    hint: '入力した数字がそのまま出ます  例: 10 → 「10」',
    icon: Hash,
    tone: 'from-emerald-50 to-teal-50 border-emerald-200 text-emerald-800',
    dot: 'bg-emerald-500',
  },
  {
    key: 'year4',
    label: '年 (YYYY)',
    hint: '発番した年を4桁で入れます  例: 2026',
    icon: CalendarStar,
    tone: 'from-purple-50 to-fuchsia-50 border-purple-200 text-purple-800',
    dot: 'bg-purple-500',
  },
  {
    key: 'year2',
    label: '年 (YY)',
    hint: '発番した年を下2桁で入れます  例: 26',
    icon: CalendarCheck,
    tone: 'from-violet-50 to-purple-50 border-violet-200 text-violet-800',
    dot: 'bg-violet-500',
  },
  {
    key: 'month',
    label: '月 (MM)',
    hint: '発番した月を2桁で入れます  例: 07',
    icon: CalendarBlank,
    tone: 'from-pink-50 to-rose-50 border-pink-200 text-pink-800',
    dot: 'bg-pink-500',
  },
  {
    key: 'serial',
    label: '連番',
    hint: '桁数と開始番号を選べます  例: 5桁 + 開始100 → 00100, 00101 …',
    icon: ListNumbers,
    tone: 'from-amber-50 to-orange-50 border-amber-200 text-amber-800',
    dot: 'bg-amber-500',
  },
];

// iter_144a · Meta lookup that still handles the auto-injected `separator`
// pseudo-type used by the trailing-separator sub-option. Not shown as a
// user-pickable tile anymore (per user "when I choose separator it
// disappeared the ITM disappeared"); users get separators as an inline
// sub-option on each category instead.
export const SEPARATOR_META = {
  key: 'separator',
  label: 'セパレーター',
  hint: '分類の間に区切り文字を入れます',
  icon: Divide,
  tone: 'from-slate-50 to-zinc-50 border-slate-300 text-slate-800',
  dot: 'bg-slate-500',
};

export const SEG_TYPE_BY_KEY = Object.fromEntries(
  [...SEG_TYPES, SEPARATOR_META].map((t) => [t.key, t])
);

// iter_144b · Deterministic label emitted on separator segments so the
// reverse round-trip is unambiguous. Testing agent flagged that the previous
// heuristic ("any 1-char non-alphanumeric literal is a separator") could
// misclassify a user's intentional 1-char symbol category. The wizard now
// stamps this exact label on every separator it emits, and `reverseSegment`
// / the collapse loop check it FIRST before falling back to the heuristic
// for legacy pre-iter_144b rules.
export const SEPARATOR_LABEL = 'セパレーター';

export const isSeparatorSeg = (seg) => (
  seg && seg.kind === 'literal' && seg.width === 1 && seg.label === SEPARATOR_LABEL
);

// ── Convert wizard state → backend segment ──────────────────────────────
export function buildSegment(wizardSeg, positionKey) {
  const t = wizardSeg.type;
  const key = positionKey;                    // 'cat1' | 'cat2' | 'cat3'
  const label = wizardSeg.label || positionKey;
  // iter_143k · Preserve per-segment description (free-text meaning explanation)
  // if the user filled it in. Stored on the segment doc so admins can retrieve
  // it later without a separate lookup.
  const description = wizardSeg.description || '';
  const meta = description ? { description } : {};
  if (t === 'letters') {
    const value = (wizardSeg.value || '').toUpperCase();
    return { key, label, kind: 'literal', value, width: value.length, ...meta };
  }
  if (t === 'digits') {
    const value = String(wizardSeg.value || '');
    return { key, label, kind: 'literal', value, width: value.length, ...meta };
  }
  if (t === 'year4') return { key, label, kind: 'year4', width: 4, ...meta };
  if (t === 'year2') return { key, label, kind: 'year2', width: 2, ...meta };
  if (t === 'month') return { key, label, kind: 'month', width: 2, ...meta };
  if (t === 'serial') {
    const w = Math.max(1, Math.min(9, parseInt(wizardSeg.width, 10) || 5));
    const start = Math.max(0, parseInt(wizardSeg.start, 10) || 0);
    return {
      key, label, kind: 'counter',
      width: w,
      min: start,
      max: Math.pow(10, w) - 1,
      ...meta,
    };
  }
  // iter_143k · separator segment — user answer to "what if I want a symbol
  // in between 分類". Emitted as a 1-char literal so no backend engine
  // changes are needed. Default char = '-'.
  // iter_144b · Label is FORCED to SEPARATOR_LABEL so reverseSegment / the
  // collapse loop can detect it without heuristics (see comment above).
  if (t === 'separator') {
    const sep = String(wizardSeg.value || '-').slice(0, 1) || '-';
    return { key, label: SEPARATOR_LABEL, kind: 'literal', value: sep, width: 1, ...meta };
  }
  return null;
}

// ── Convert backend segment → wizard state (for editing existing families) ─
export function reverseSegment(seg) {
  const kind = seg.kind;
  const description = seg.description || '';
  if (kind === 'literal') {
    const value = String(seg.value || '');
    const isDigits = /^\d+$/.test(value);
    // iter_144b · Deterministic detection first — if the label is exactly
    // SEPARATOR_LABEL then this is a separator emitted by iter_144b+ code,
    // regardless of what character it holds. Falls back to the legacy
    // heuristic (1-char non-alphanumeric literal without the marker label)
    // so pre-marker rows still round-trip cleanly.
    if (isSeparatorSeg(seg)) {
      return { type: 'separator', label: seg.label, value, description };
    }
    const looksLikeSeparator = value.length === 1 && !/[a-zA-Z0-9]/.test(value);
    const type = looksLikeSeparator ? 'separator' : (isDigits ? 'digits' : 'letters');
    return { type, label: seg.label, value, description };
  }
  if (kind === 'year4') return { type: 'year4', label: seg.label, description };
  if (kind === 'year2') return { type: 'year2', label: seg.label, description };
  if (kind === 'month') return { type: 'month', label: seg.label, description };
  if (kind === 'counter') {
    return { type: 'serial', label: seg.label, width: seg.width || 5, start: seg.min || 0, description };
  }
  return null;
}

// Human-preview text for one segment (best-effort, matches backend semantics)
export function previewSegment(wizardSeg) {
  const t = wizardSeg.type;
  if (t === 'letters') return (wizardSeg.value || 'AA').toUpperCase();
  if (t === 'digits') return String(wizardSeg.value || '00');
  if (t === 'year4') return String(new Date().getFullYear());
  if (t === 'year2') return String(new Date().getFullYear() % 100).padStart(2, '0');
  if (t === 'month') return String(new Date().getMonth() + 1).padStart(2, '0');
  if (t === 'serial') {
    const w = Math.max(1, parseInt(wizardSeg.width, 10) || 5);
    const start = Math.max(0, parseInt(wizardSeg.start, 10) || 0);
    return String(start).padStart(w, '0');
  }
  // iter_143k · Separator preview — echo the chosen char (default '-').
  if (t === 'separator') return String(wizardSeg.value || '-').slice(0, 1) || '-';
  return '?';
}

// Derive a machine-safe SKU rule code from a display name. The backend
// enforces `^[a-z0-9_]{2,32}$`, so we strip anything outside ASCII a–z / 0–9
// early (including Japanese kana / kanji, hyphens, spaces, punctuation).
// If the name is Japanese-only, the derived slug is empty — the caller
// falls back to `randomCode()` so the user never has to invent an ASCII slug.
export function slugify(name) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32);
}

// Random fallback code for Japanese-only rule names. Uses lowercase
// alphanumeric only (no _) so the result always matches CODE_RE and stays
// URL-friendly. Format: `tpl_a3f9k2` (8 chars total).
export function randomCode() {
  const s = Math.random().toString(36).slice(2, 8);
  return `tpl_${s}`;
}

// Same rule the backend enforces on template.code. Used to inline-validate
// the wizard's Step-1 field so users can't reach the preview screen with an
// invalid code.
export const CODE_RE = /^[a-z0-9_]{2,32}$/;
